import { useEffect, useMemo, useState } from 'react'
import StatusMenu from './StatusMenu.jsx'
import {
  formatMoney, formatMiles, ageLabel, noteFor, isFollowUpDue,
  STATUS_LABELS, LINK_HEALTH_INFO,
} from '../util.js'

/**
 * A dense, paged list for the sections that grow without bound — Tracked, Gone,
 * Dismissed. Cards are right for comparing a handful side by side and wrong for
 * scanning 170.
 */
export default function ListingTable({ listings, notes, onOpen, onStatus, pageSize = 25 }) {
  const [page, setPage] = useState(0)

  // Identity of *which listings* this is, not identity of the array object.
  // `listings` is very often rebuilt by the parent (App.jsx recomputes its
  // section arrays with useMemo keyed on `data.notes`, which is a new object
  // on every Firebase write anywhere in /notes) even when this section's
  // membership hasn't changed at all. Resetting on every new array reference
  // would knock the operator back to page 1 while they're paging through
  // Tracked just because someone set a status on an unrelated Working bike —
  // see FilterBar.jsx's canonicalKey comment for the same trap. Keying on the
  // ids (in order) instead means the effect only fires when the actual set —
  // or its order — of listings on this page has changed.
  const idsKey = useMemo(() => listings.map((l) => l.id).join('|'), [listings])

  // Back to the first page whenever the underlying set of listings changes.
  // Without this, filtering down to 12 while on page 5 renders an empty
  // table, which reads as "no results" when there are twelve.
  useEffect(() => setPage(0), [idsKey])

  const pages = Math.max(1, Math.ceil(listings.length / pageSize))
  const current = Math.min(page, pages - 1)
  const start = current * pageSize
  const rows = listings.slice(start, start + pageSize)

  return (
    <div className="table-wrap">
      <table className="listing-table">
        <thead>
          <tr>
            <th>Bike</th>
            <th>Price</th>
            <th>OTD</th>
            <th>Mileage</th>
            <th>Dist</th>
            <th>Dealer</th>
            <th>Age</th>
            <th aria-label="Link" />
            <th aria-label="Follow-up" />
            <th aria-label="Status" />
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td className="muted" colSpan={11}>No listings</td>
            </tr>
          )}
          {rows.map((l) => {
            const note = noteFor(notes, l.id)
            // 'unchecked' is a display-only fallback, not a crawler-written
            // state (see LINK_HEALTH_STATES) — otherwise a never-checked row
            // rendered nothing, indistinguishable from a row with no chip at all.
            const healthState = l.linkHealth?.state || 'unchecked'
            const health = LINK_HEALTH_INFO[healthState]
            return (
              <tr key={l.id}>
                <td>
                  <button className="row-open" onClick={() => onOpen?.(l)}>
                    {l.year} {l.trim}
                  </button>
                </td>
                <td className="num">{formatMoney(l.price)}</td>
                <td className="num">{note.otdQuote != null ? formatMoney(note.otdQuote) : '—'}</td>
                <td className="num">{formatMiles(l.mileage)}</td>
                <td className="num">{l.distanceMiles == null ? '—' : `${Math.round(l.distanceMiles)} mi`}</td>
                <td>{l.dealerName || '—'}</td>
                <td className="muted">{ageLabel(l) || '—'}</td>
                <td><span className={`health h-${healthState}`} title={health.text}>{health.icon}</span></td>
                <td>
                  {isFollowUpDue(note) && (
                    <span className="followup" title="Follow-up due">⏰</span>
                  )}
                </td>
                <td>
                  {note.status && (
                    <span className={`status-pill s-${note.status}`}>{STATUS_LABELS[note.status]}</span>
                  )}
                </td>
                <td><StatusMenu onStatus={(s) => onStatus?.(l, s)} /></td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="pager">
        {listings.length > pageSize && (
          <button onClick={() => setPage(current - 1)} disabled={current === 0}>
            ‹ Previous
          </button>
        )}
        <span className="count-line">
          {listings.length === 0 ? 0 : start + 1}–{Math.min(start + pageSize, listings.length)} of {listings.length}
        </span>
        {listings.length > pageSize && (
          <button onClick={() => setPage(current + 1)} disabled={current >= pages - 1}>
            Next ›
          </button>
        )}
      </div>
    </div>
  )
}
